import { CommonModule } from '@angular/common';
import { Inject, Injectable, NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { RecaptchaSettings, RECAPTCHA_SETTINGS } from 'ng-recaptcha';
import { Observable } from 'rxjs';

import { RtAnalyticsModule } from '@recruitee/analytics';
import { AccountBase, APP_CONFIG, COMPANY_ID, RtcErrorHandlerModule } from '@recruitee/common';
import {
  AuthService,
  HttpApiErrorHandlerService,
  HttpApiService,
  HttpRequestData,
  RtHttpModule,
} from '@recruitee/http';
import { I18nModule, LANGUAGE_CONFIG, LOCALE } from '@recruitee/i18n';
import { RtPlatform, RtUIModule, UiProvidersModule } from '@recruitee/user-interface';
import { RtWysiwygModule, WysiwygUploader } from '@recruitee/wysiwyg';

import { environment } from '../environments/environment';
import { authService } from '../main-deps';
import { AppComponent } from './app.component';
import { AppRevisionProvider, AppRevisionToken } from './app.revision';
import { appConfig, languageConfig } from './config';
import { ReferralsRequestData } from './config/referrals-request-data';
import { CoreModule } from './core/core.module';
import { ReferralsHttpApiErrorHandlerService } from './core/http-api-error-handler.service';
import { MainViewModule } from './core/main-view/main-view.module';
import { ApplicationsDataModule } from './features/applications/data/applications-data.module';
import { ConditionsDataModule } from './features/conditions/data/conditions-data.module';
import { CoreDataModule } from './features/core/data/core-data.module';
import { FeatureDiscoveryDataModule } from './features/feature-discovery/data/feature-discovery-data.module';
import { MainViewDataModule } from './features/main-view/data/main-view-data.module';
import { OfferAssignmentsDataModule } from './features/offer-assignments/data/offer-assignments-data.module';
import { ProfileDataModule } from './features/profile/data/profile-data.module';
import { ProgramDataModule } from './features/program/data/program-data.module';
import { QuestionsTemplatesDataModule } from './features/questions-templates/data/questions-templates-data.module';
import { ReferrerDataModule } from './features/referrer/data/referrer-data.module';
import { RewardsDataModule } from './features/rewards/data/rewards-data.module';
import { TermsDataModule } from './features/terms/data/terms-data.module';
import { RoutingModule } from './routing/routing.module';
import { SharedModule } from './shared/shared.module';

@Injectable()
export class RtrWysiwygUploader implements WysiwygUploader {
  constructor(private httpApi: HttpApiService) {}

  public upload(file: File): Observable<{ fileUrl: string }> {
    const data = new FormData();
    data.append('upload[file]', file);
    return this.httpApi.post('/uploads', null, data);
  }
}

@Injectable()
export class Account extends AccountBase {}

@NgModule({
  imports: [
    MainViewModule,
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    RoutingModule,
    CoreModule,
    SharedModule,
    ReactiveFormsModule,
    RtAnalyticsModule.forRoot({
      disable: !environment.analytics,
      segmentKey: environment.analyticsKey,
      trackRouteChange: true,
    }),
    RtUIModule,
    UiProvidersModule.forRoot(),
    I18nModule.forPhraseApp({
      phraseAppProjectId: environment.phraseAppProjectId,
      editor: true,
    }),
    RtcErrorHandlerModule.forRoot(
      {
        enable: environment.prodMode,
        dsn: environment.sentry_dsn,
        revisionToken: AppRevisionToken,
      },
      Account,
    ),
    RtWysiwygModule.forRoot({
      provide: WysiwygUploader,
      useClass: RtrWysiwygUploader,
    }),
    EffectsModule.forRoot([]),
    StoreModule.forRoot(
      {},
      {
        runtimeChecks: {
          strictStateSerializability: false,
          strictActionSerializability: false,
          strictStateImmutability: false,
          strictActionImmutability: false,
        },
      },
    ),
    RtHttpModule.forRoot({
      provide: HttpRequestData,
      useClass: ReferralsRequestData,
    }),
    RewardsDataModule,
    ProgramDataModule,
    OfferAssignmentsDataModule,
    ApplicationsDataModule,
    ConditionsDataModule,
    FeatureDiscoveryDataModule,
    ProfileDataModule,
    QuestionsTemplatesDataModule,
    TermsDataModule,
    ReferrerDataModule,
    CoreDataModule,
    MainViewDataModule,
  ],
  declarations: [AppComponent],
  providers: [
    Account,
    AppRevisionProvider,
    { provide: COMPANY_ID, useValue: '' },
    { provide: LOCALE, useValue: languageConfig.defaultLanguage },
    { provide: APP_CONFIG, useValue: appConfig },
    { provide: LANGUAGE_CONFIG, useValue: languageConfig },
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: <RecaptchaSettings>{ siteKey: appConfig.recaptchaSiteKey, size: 'invisible' },
    },
    {
      provide: HttpApiErrorHandlerService,
      useClass: ReferralsHttpApiErrorHandlerService,
      deps: [AuthService, [Inject, COMPANY_ID]],
    },
    {
      provide: AuthService,
      useValue: authService,
    },
    RtPlatform,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
